import type { StatusKey } from "@/components/status-badge";

/** The billing facts a customer status is derived from (organization billing overview). */
export interface BillingStatusInput {
  /** Effective access state from the server (`getEffectiveAccessState`). */
  state: string;
  /** Stripe subscription status, when a subscription exists. */
  billingStatus: string | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: number | null;
  /** Expiry of a trial or free-access grant from Construction Cortex. */
  grantExpiresAt?: number | null;
  graceEndsAt?: number | null;
  isInvoiced?: boolean;
  /** The organization has a Stripe subscription (not just a Stripe customer). */
  hasStripeBilling?: boolean;
}

export type BillingNextAction = "start" | "continue_checkout" | "manage" | "contact_support" | null;

export interface CustomerBillingStatus {
  label: string;
  tone: StatusKey;
  description: string;
  /** The one date that matters next. Trial ends and cancellations are never called renewals. */
  date: { label: string; value: number } | null;
  nextAction: BillingNextAction;
}

const ENDED_ACCESS_NOTE = "You can still view projects and download completed results.";

function dated(label: string, value: number | null | undefined) {
  return value ? { label, value } : null;
}

/**
 * Customer-facing billing status. One mapping for the organization billing card and the
 * platform-admin "customer sees" preview, so both describe the same state the same way.
 */
export function describeBillingStatus(input: BillingStatusInput): CustomerBillingStatus {
  const manageOrNull: BillingNextAction = input.hasStripeBilling ? "manage" : null;

  switch (input.state) {
    case "suspended":
      return {
        label: "Suspended",
        tone: "error",
        description: `Editing and processing are paused for your company. Contact support to restore access. ${ENDED_ACCESS_NOTE}`,
        date: null,
        nextAction: "contact_support",
      };
    case "payment_grace":
      return describeGrace(input);
    case "internal_trial":
      return {
        label: "Trial",
        tone: "review",
        description:
          "Your company has a trial from Construction Cortex. Start a subscription before it ends to keep creating, editing, and processing work.",
        date: dated("Trial ends", input.grantExpiresAt),
        nextAction: "start",
      };
    case "free_access":
      return {
        label: "Free access",
        tone: "completed",
        description: "Construction Cortex has given your company free access. No subscription is needed.",
        date: dated("Access ends", input.grantExpiresAt),
        nextAction: null,
      };
    case "paid_active":
      return describePaidActive(input);
    case "expired":
      return {
        label: "Access ended",
        tone: "error",
        description: `Your company can't create, edit, or process work right now. ${ENDED_ACCESS_NOTE}`,
        date: null,
        // A subscription still recovering a failed payment is fixed in billing, not by buying again.
        nextAction:
          input.billingStatus === "past_due" || input.billingStatus === "unpaid" ? manageOrNull : "start",
      };
    case "unpaid_setup":
    default:
      return describeUnpaid(input);
  }
}

function describeGrace(input: BillingStatusInput): CustomerBillingStatus {
  // Manual grace is a grant from Construction Cortex, not a failed payment.
  if (!input.hasStripeBilling) {
    return {
      label: "Payment needed",
      tone: "pending",
      description: "Your company has temporary access while billing is sorted out. Contact support if you need help.",
      date: dated("Temporary access ends", input.graceEndsAt),
      nextAction: "contact_support",
    };
  }
  if (input.isInvoiced) {
    return {
      label: "Invoice overdue",
      tone: "pending",
      description: "An invoice for your subscription is past due. Pay it to keep creating, editing, and processing work.",
      date: dated("Pay by", input.graceEndsAt),
      nextAction: "manage",
    };
  }
  return {
    label: "Payment needed",
    tone: "pending",
    description: "Your last payment didn't go through. Update your payment method to keep creating, editing, and processing work.",
    date: dated("Update payment by", input.graceEndsAt),
    nextAction: "manage",
  };
}

function describePaidActive(input: BillingStatusInput): CustomerBillingStatus {
  if (input.cancelAtPeriodEnd || input.billingStatus === "canceled") {
    return {
      label: "Canceled",
      tone: "pending",
      description: "Your subscription won't renew. Your team keeps full access until the paid period ends.",
      date: dated("Access ends", input.currentPeriodEnd),
      nextAction: "manage",
    };
  }
  if (input.billingStatus === "trialing") {
    // Stripe trialing here is free time Construction Cortex added to an existing subscription.
    return {
      label: "Free period",
      tone: "review",
      description: "Construction Cortex added free time to your subscription. Billing starts again automatically.",
      date: dated("Billing starts", input.currentPeriodEnd),
      nextAction: "manage",
    };
  }
  return {
    label: "Active",
    tone: "completed",
    description: input.isInvoiced
      ? "Your subscription is billed by invoice and renews automatically."
      : "Your subscription renews automatically.",
    date: dated("Renews", input.currentPeriodEnd),
    nextAction: "manage",
  };
}

function describeUnpaid(input: BillingStatusInput): CustomerBillingStatus {
  if (input.billingStatus === "checkout_open") {
    return {
      label: "Checkout open",
      tone: "processing",
      description: "Finish checkout to activate your subscription. You won't be charged until you do.",
      date: null,
      nextAction: "continue_checkout",
    };
  }
  if (input.billingStatus === "incomplete" || input.billingStatus === "incomplete_expired") {
    return {
      label: "Payment not completed",
      tone: "pending",
      description: "Your first payment didn't go through, so the subscription hasn't started. Try again with another payment method.",
      date: null,
      nextAction: "start",
    };
  }
  return {
    label: "No subscription",
    tone: "pending",
    description:
      "Start a subscription so your team can create projects, upload documents, and process work. You can invite your team now.",
    date: null,
    nextAction: "start",
  };
}

export function formatBillingDate(value: number) {
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
